import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface PredictedGrade {
  subject: string;
  grade: string;
}

export interface UserPreferences {
  theme: 'light' | 'dark' | 'system';
  notifications: boolean;
  language: string;
  university?: string;
  course?: string;
  accommodation?: string;
  yearOfStudy?: string;
  // Journey data properties
  predictedGrades?: PredictedGrade[];
  interestedSubjects?: string[];
  targetYear?: string;
  savedUniversities?: string[];
  savedCourses?: string[];
  activity?: any[];
}

interface UserPrefsContextType {
  preferences: UserPreferences;
  updatePreferences: (prefs: Partial<UserPreferences>) => void;
  resetPreferences: () => void;
  // Aliases for backward compatibility with UniJourneyBar
  prefs: UserPreferences;
  updateSection: (key: keyof UserPreferences, value: any) => void;
  isAuthenticated: boolean;
}

const defaultPreferences: UserPreferences = {
  theme: 'system',
  notifications: true,
  language: 'en',
};

const UserPrefsContext = createContext<UserPrefsContextType | undefined>(undefined);

export function UserPrefsProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<UserPreferences>(defaultPreferences);

  useEffect(() => {
    // Load preferences from localStorage on mount
    const savedPrefs = localStorage.getItem('userPreferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences({ ...defaultPreferences, ...parsed });
      } catch (error) {
        console.error('Failed to parse saved preferences:', error);
      }
    }
  }, []);

  const updatePreferences = (newPrefs: Partial<UserPreferences>) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    localStorage.setItem('userPreferences', JSON.stringify(updated));
  };

  const resetPreferences = () => {
    setPreferences(defaultPreferences);
    localStorage.removeItem('userPreferences');
  };

  // Alias methods for backward compatibility
  const updateSection = (key: keyof UserPreferences, value: any) => {
    updatePreferences({ [key]: value });
  };

  return (
    <UserPrefsContext.Provider value={{ 
      preferences, 
      updatePreferences, 
      resetPreferences,
      // Aliases for backward compatibility
      prefs: preferences,
      updateSection,
      isAuthenticated: true // Default to true for now, could be integrated with AuthContext
    }}>
      {children}
    </UserPrefsContext.Provider>
  );
}

export function useUserPrefs() {
  const context = useContext(UserPrefsContext);
  if (context === undefined) {
    throw new Error('useUserPrefs must be used within a UserPrefsProvider');
  }
  return context;
}
